import React, { useCallback, useMemo } from 'react';
import { MatrixWidget } from './index';
import { NonIdealState } from '@blueprintjs/core';
import { selectDilDefinitionByName } from 'srs-middleware';
import { useFacetsState } from 'hooks/useFacetsState';
import { Dimensions, NameCountValue } from './../../interfaces';
import { MatrixProps, MatrixValue, EndpointProps, CellValue } from './interfaces'; 
import { formatValue, NO_DATA_MESSAGE } from '../chartUtils.js';

/**
 * ChartWrapper for the Matrix.
 *
 * @param dimensions
 * @param data
 * @param selectedDefinitionName
 * @param endpointQuery
 * @param widgetConfiguration
 * @returns {*}
 * @constructor
 */

export const MatrixWrapper: React.FC<MatrixWrapperProps> = (({ dimensions, data, selectedDefinitionName, endpointQuery, widgetConfiguration, noDataMessage=NO_DATA_MESSAGE }) => {
	// get the actual definition
	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	const convertedData: MatrixValue = useMemo(() => {
		if (
			!data ||
			!selectedDefinition ||
			!endpointQuery?.options?.propertyList || 
			!Object.keys(data).includes('matrix') || 
			!Object.keys(data).includes('rows') ||
			!Object.keys(data).includes('columns')
		) {
			return;
		}

		let summary_cell_value = formatValue(data['summary']['value']);
		let matrix = data.matrix
		const fill_in_zeros: NameCountValue[] = data['columns'].map((entry, index) => {return { 'name': entry['name'], 'count': 0, 'value': 0 }}); 
		Object.keys(data['matrix']).forEach((row, index) => {
			data['matrix'][row] = fill_in_zeros.map(entry => {
				let found_entry = Object.values(data['matrix'][row]).find(obj => obj['name'] === entry['name'])
				return found_entry ? found_entry : entry; 
			})
		});

		let aggregationTypeRow="All " + endpointQuery.options.propertyList[0];
    	let aggregationTypeCol="All " + endpointQuery.options.propertyList[1];

		let newMatrix = []; // array of rows (also an array)
		// header row: [ units (if applicable), col aggregation type, column headers...]
		let column_headers = ["", aggregationTypeRow, ...Object.values(data['columns']).map(col => col['name'])]
		// second row: [ row aggregation type, summary cell value (i.e. 0,0 cell), summary value for each column]
		newMatrix.push([aggregationTypeCol, summary_cell_value, ...Object.values(data['columns']).map(col => formatValue(col['value']))])
		// third row on: [ row label, row summary value, values for each row/col cell ]
		Object.keys(matrix).forEach((row_key, index) => {
			newMatrix.push([row_key, formatValue(data['rows'][index]['value']), ...Object.values(matrix[row_key]).map(row => formatValue(row['value']))])
		})
		
		return {'column_headers': column_headers, 'matrix': newMatrix}; 
	}, [data, selectedDefinition, endpointQuery]);

	//If the state is not ideal (the data is null or empty) render the nonIdealState message
	return (convertedData && Object.keys(convertedData).length == 2) ? (
		<MatrixWidget
			data={convertedData}
			widgetConfiguration={widgetConfiguration}
		/>
	) : (
		<NonIdealState
			title="No Data Found"
			icon="graph-remove"
			description={noDataMessage}
		/>
	);
})

interface MatrixWrapperProps {
    /**
     * Array of NameValue objects representing the data to display in the donut
     */
    data: MatrixValue;
	noDataMessage: string;
    dimensions: Dimensions; 
    selectedDefinitionName: string; 
    endpointQuery: EndpointProps; 

    /**
     * The widget config object
     */
    widgetConfiguration: object;
}
